import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Loader2, AlertTriangle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Drawer showing the original quiz submission linked to a report.
 * Used for support disputes / audit — surfaces exact answers, computed
 * archetypes, and flags any mismatch between the submission and the PDF.
 */
export function SubmissionAuditDrawer({ report, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!report) { setData(null); setErr(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const res = await fetch(`${API_URL}/api/admin/reports/${report.id}/submission`);
        if (!res.ok) throw new Error('Lookup failed');
        const payload = await res.json();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load submission');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [report]);

  const submission = data?.submission;
  const results = submission?.results || {};
  const userData = submission?.user_data || {};

  return (
    <Sheet open={!!report} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-[#0a0a0a] border-gray-800 text-gray-100" data-testid="submission-audit-drawer">
        <SheetHeader>
          <SheetTitle className="text-gray-100">Quiz Submission Audit</SheetTitle>
          <SheetDescription className="text-xs text-gray-500">
            Original quiz answers linked to this report — useful for support disputes and archetype-mismatch investigation.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading submission…
          </div>
        )}
        {err && <div className="mt-6 text-sm text-red-400">{err}</div>}

        {!loading && !err && data && !submission && (
          <div className="mt-6 text-sm text-gray-400">
            {data.message || 'No linked quiz submission found.'}
          </div>
        )}

        {!loading && !err && report && submission && (
          <div className="mt-6 space-y-6 text-sm">
            {data.mismatch && (
              <div className="p-3 rounded-md border border-yellow-600 bg-yellow-900/20 flex items-start gap-2 text-yellow-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Archetype mismatch detected</div>
                  <div className="text-xs mt-1">
                    Report shows {data.report_primary}/{data.report_secondary} but the
                    linked submission computed {results.dominant}/{results.secondary}.
                  </div>
                </div>
              </div>
            )}

            <Section title="Report vs Submission">
              <Row k="Report archetypes" v={`${data.report_primary} / ${data.report_secondary}`} />
              <Row k="Submission computed" v={`${results.dominant || '—'} / ${results.secondary || '—'}`} />
              <Row k="Submission ID" v={<code className="text-xs">{data.submission_id || '—'}</code>} />
              <Row k="Submitted at" v={submission.submitted_at || '—'} />
            </Section>

            <Section title="User">
              <Row k="Name" v={userData.name || '—'} />
              <Row k="Email" v={userData.email || '—'} />
              <Row k="Company" v={userData.company_name || '—'} />
            </Section>

            <Section title="Personality answers">
              <AnswerGrid answers={submission.personality_answers} />
            </Section>

            <Section title="Trait answers">
              <AnswerGrid answers={submission.trait_answers} />
            </Section>

            <Section title="Visual preferences">
              <div className="flex flex-wrap gap-1">
                {(submission.visual_vibes || []).map((v, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-gray-800 text-gray-200 text-xs">
                    {v}
                  </span>
                ))}
                {(submission.visual_vibes || []).length === 0 && <span className="text-gray-500 text-xs">none</span>}
              </div>
            </Section>

            {(() => {
              // allScores may come back as an array [{archetype, count, weightedScore}, ...]
              // OR a plain dict {"Ruler": 10, "Sage": 8}. Normalise to a sorted list.
              const raw = results.allScores;
              let scoresList = [];
              if (Array.isArray(raw)) {
                scoresList = raw.slice();
              } else if (raw && typeof raw === 'object') {
                scoresList = Object.entries(raw).map(([archetype, v]) => (
                  typeof v === 'object' && v !== null
                    ? { archetype, ...v }
                    : { archetype, weightedScore: v, count: v }
                ));
              }
              scoresList.sort((a, b) =>
                (b.weightedScore ?? b.count ?? 0) - (a.weightedScore ?? a.count ?? 0)
              );
              if (scoresList.length === 0) return null;
              return (
                <Section title="Archetype scores">
                  <div className="space-y-1">
                    {scoresList.slice(0, 6).map((s) => (
                      <div key={s.archetype} className="flex justify-between items-center text-xs">
                        <span className="text-gray-300">{s.archetype}</span>
                        <span className="text-gray-400 tabular-nums">
                          {typeof s.weightedScore === 'number' ? s.weightedScore.toFixed(1) : s.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>
              );
            })()}

            <a
              href={report?.id ? `${API_URL}/api/admin/reports/${report.id}/download` : '#'}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
              data-testid="drawer-download-link"
            >
              Download this PDF <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
      {title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between items-start gap-4 text-xs">
    <span className="text-gray-400">{k}</span>
    <span className="text-gray-200 text-right break-all">{v || '—'}</span>
  </div>
);

const AnswerGrid = ({ answers }) => {
  if (!Array.isArray(answers) || answers.length === 0) {
    return <div className="text-xs text-gray-500">No answers stored.</div>;
  }
  return (
    <div className="grid grid-cols-8 gap-1 text-[10px]">
      {answers.map((a, i) => (
        <div
          key={i}
          className={`px-1 py-0.5 rounded text-center ${
            a === 'A' ? 'bg-blue-900/40 text-blue-300' : 'bg-purple-900/40 text-purple-300'
          }`}
          title={`Q${i + 1}: ${a}`}
        >
          {a}
        </div>
      ))}
    </div>
  );
};
